/**
 * 金额与 CU 计算统一走 decimal.js（TECH-BRIEF §1：禁止用 JS number 参与金额或 CU 计算）。
 *
 * - 全局舍入模式为 ROUND_DOWN（向零截断）：PRD §2.2.3「统一截断、不进位，显示值永远不超过实际值」。
 * - 账本 8 位小数（PRD §2.2.1、§8.2）；用户端列表 USDT / CU 2 位、价格 4 位（PRD §2.2.3）。
 * - 有效位数取 64，远大于 NUMERIC(38,8) 的 38 位，乘除中间结果不会丢精度。
 * - 关闭科学计数法输出，保证 toString() 永远是普通十进制串，可直接写入 NUMERIC。
 */
import DecimalModule from 'decimal.js';
import type { Decimal as DecimalInstance } from 'decimal.js';

// decimal.js 的类型声明按 CJS 解析（包未声明 "type": "module"），在 NodeNext 下默认导入的类型是模块对象；
// 运行时 ESM 入口 decimal.mjs 的默认导出就是 Decimal 类。这里统一取到类本身。
const DecimalJs = ((DecimalModule as unknown as { default?: typeof DecimalInstance }).default ??
  DecimalModule) as unknown as typeof DecimalInstance;
type DecimalJs = DecimalInstance;

DecimalJs.set({
  precision: 64,
  rounding: DecimalJs.ROUND_DOWN,
  toExpNeg: -64,
  toExpPos: 64,
});

export const Decimal = DecimalJs;
export type Decimal = DecimalJs;

/** 允许参与金额计算的输入。故意不包含 number。 */
export type DecimalInput = DecimalJs | string | bigint;

/** 账本精度（PRD §2.2.1：账本精度 8 位小数）。 */
export const LEDGER_PLACES = 8;
/** 用户端列表显示精度（PRD §2.2.3）。 */
export const USDT_LIST_PLACES = 2;
export const CU_LIST_PLACES = 2;
export const PRICE_LIST_PLACES = 4;

const DECIMAL_STRING = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;

/** 把输入转成 Decimal；拒绝 number、空串、NaN 与 ±Infinity。 */
export function dec(value: DecimalInput): DecimalJs {
  if (value instanceof DecimalJs) {
    if (!value.isFinite()) throw new RangeError('Decimal value must be finite');
    return value;
  }
  if (typeof value === 'bigint') return new DecimalJs(value.toString());
  if (typeof value !== 'string') {
    throw new TypeError('Decimal input must be a string, bigint or Decimal');
  }
  const trimmed = value.trim();
  if (!DECIMAL_STRING.test(trimmed)) {
    throw new RangeError(`Invalid decimal string: "${value}"`);
  }
  return new DecimalJs(trimmed);
}

function assertPlaces(places: number): void {
  if (!Number.isInteger(places) || places < 0 || places > 20) {
    throw new RangeError(`places must be an integer in [0, 20], got ${String(places)}`);
  }
}

/** 向零截断到指定小数位；结果为 0 时去掉负号。 */
export function truncate(value: DecimalInput, places: number): DecimalJs {
  assertPlaces(places);
  const result = dec(value).toDecimalPlaces(places, DecimalJs.ROUND_DOWN);
  return result.isZero() ? new DecimalJs(0) : result;
}

/** 截断后按固定小数位输出字符串，例如 truncateToString('1.239', 2) === '1.23'。 */
export function truncateToString(value: DecimalInput, places: number): string {
  return truncate(value, places).toFixed(places, DecimalJs.ROUND_DOWN);
}

/** USDT 列表显示：2 位小数，截断（PRD §2.2.3）。 */
export function toUsdt2(value: DecimalInput): string {
  return truncateToString(value, USDT_LIST_PLACES);
}

/** CU 列表显示：2 位小数，截断（PRD §2.2.3）。 */
export function toCu2(value: DecimalInput): string {
  return truncateToString(value, CU_LIST_PLACES);
}

/** CU 价格列表显示：4 位小数，截断（PRD §2.2.3、§2.3.2）。 */
export function toPrice4(value: DecimalInput): string {
  return truncateToString(value, PRICE_LIST_PLACES);
}

/** 账本入库：8 位小数，截断（PRD §8.2「8 位小数截断」）。 */
export function toLedger8(value: DecimalInput): string {
  return truncateToString(value, LEDGER_PLACES);
}
